import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Play, ShieldCheck, Lock, TrendingUp } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/17485657/pexels-photo-17485657.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const EASE = [0.16, 1, 0.3, 1];

export const Hero = () => {
  const { t } = useTranslation();
  const lines = t("hero.lines", { returnObjects: true });
  const ref = useRef(null);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative pt-36 lg:pt-44 pb-20 lg:pb-28 overflow-hidden">
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-mint/10 blur-[160px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.15fr_1fr] gap-16 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full border border-mint/30 bg-mint/10 px-4 py-1.5 mb-8"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-mint animate-pulse" />
            <span data-testid="hero-badge" className="text-xs font-medium text-mint tracking-wide">{t("hero.badge")}</span>
          </motion.div>

          <h1 className="font-display font-semibold tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: EASE }}
                  className={`block ${i === 2 ? "text-mint" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
            data-testid="hero-subtitle"
            className="mt-6 text-base md:text-lg text-white/60 max-w-xl leading-relaxed"
          >
            {t("hero.sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#waitlist"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-mint px-7 py-3.5 text-sm font-semibold text-night transition-transform duration-200 hover:scale-105 glow-mint"
            >
              {t("hero.ctaPrimary")}
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={2} />
            </a>
            <a
              href="#demo"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-sm font-medium text-white/80 transition-colors duration-200 hover:border-mint/50 hover:text-white"
            >
              <Play className="w-4 h-4" strokeWidth={1.5} />
              {t("hero.ctaSecondary")}
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            data-testid="hero-micro"
            className="mt-6 text-xs text-white/40 tracking-wide"
          >
            {t("hero.micro")}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
          style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d", perspective: 1000 }}
          className="relative hidden lg:block"
        >
          <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl" style={{ clipPath: "inset(0 round 24px)" }}>
            <img src={HERO_IMG} alt="Wadöo" className="w-full h-[440px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-night via-night/20 to-transparent" />
          </div>

          <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -left-8 top-10 rounded-2xl border border-white/10 bg-night/80 backdrop-blur-xl px-5 py-4"
            style={{ transform: "translateZ(50px)" }}
          >
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-mint" strokeWidth={1.5} />
              <div>
                <p className="text-lg font-display font-semibold text-white leading-none">128</p>
                <p className="text-[11px] text-white/50 mt-1">{t("hero.cardRequests")}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute -right-6 top-1/3 rounded-2xl border border-white/10 bg-night/80 backdrop-blur-xl px-5 py-4"
            style={{ transform: "translateZ(70px)" }}
          >
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-mint" strokeWidth={1.5} />
              <div>
                <p className="text-lg font-display font-semibold text-white leading-none">AES-256</p>
                <p className="text-[11px] text-white/50 mt-1">{t("hero.cardEncrypted")}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            className="absolute left-10 -bottom-6 rounded-2xl border border-mint/20 bg-night/80 backdrop-blur-xl px-5 py-4"
            style={{ transform: "translateZ(60px)" }}
          >
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-mint" strokeWidth={1.5} />
              <div>
                <p className="text-lg font-display font-semibold text-mint leading-none">≈ 240 €</p>
                <p className="text-[11px] text-white/50 mt-1">{t("hero.cardValue")}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
